/*
 * PanelManageModal: ein sinnux-Touchpanel aus der Ferne einstellen, ähnlich
 * dem TP Configurator. Spricht nur manage.v1 (docs/wire-device-management.md
 * 6.3): jede Lese- und Schreibaktion ist eine signierte Anfrage, die der Client
 * über die Freigabeschleuse an NetPilot weitergibt.
 *
 * Ablauf je Aktion: POST .../manage/requests, dann GET /api/manage/requests/{id}
 * nachfragen, bis die Antwort da ist. Netzänderungen antworten mit 202 und
 * confirmToken; die Bestätigung schickt der Server selbst, sobald das Panel
 * nach der Umstellung wieder signiert antwortet (sonst Rücknahme nach 120 s).
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { showToast } from './Toaster';
import './hv-modal.css';
import './PanelManageModal.css';

interface Props {
  plantId: string;
  clientId: string;
  clientLabel: string;
  onClose(): void;
}

interface ManageRequestDto {
  correlationId: string;
  state: string;
  status: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  body: unknown;
  contentType: string | null;
  blobSha256: string | null;
  netChangeState: string | null;
  hardeningEffective: string | null;
  followUps?: ManageRequestDto[] | null;
}

interface ManageClientState {
  serverKeyConfigured: boolean;
  deviceKeyPresent: boolean;
}

interface Answer<T> {
  status: number;
  body: T;
  request: ManageRequestDto;
}

class ManageFailure extends Error {}

const ERRORS: Record<string, string> = {
  hardening: 'Das Panel steht nicht auf Härtung „werkstatt“. Netzänderungen sind gesperrt.',
  gate_missing: 'Auf dem Panel fehlt die Freigabeschleuse. Älteres sinnux-Image?',
  not_supported: 'Das kann dieses Panel nicht.',
  nothing_pending: 'Es liegt kein Update zum Einspielen bereit.',
  rejected: 'Das Panel hat den Wert abgelehnt.',
  timeout: 'Das Panel hat nicht rechtzeitig geantwortet.',
};

async function send<T>(plantId: string, clientId: string, method: string, path: string,
                       body?: unknown, reason?: string): Promise<Answer<T>> {
  let sent: { correlationId: string };
  try {
    sent = await api.post<{ correlationId: string }>(
      `/api/plants/${plantId}/clients/${clientId}/manage/requests`,
      { method, path, body: body ?? null, reason: reason ?? null, timeoutSeconds: 30 });
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) throw new ManageFailure('Dafür fehlt dir die Berechtigung.');
    if (e instanceof ApiError && e.status === 503) throw new ManageFailure('Gerätefernverwaltung ist auf dieser Instanz nicht eingeschaltet.');
    throw new ManageFailure('Anfrage konnte nicht abgesetzt werden.');
  }
  const until = Date.now() + 45_000;
  while (Date.now() < until) {
    await new Promise((r) => setTimeout(r, 700));
    const r = await api.get<ManageRequestDto>(`/api/manage/requests/${sent.correlationId}`);
    if (r.state === 'sent') continue;
    if (r.state !== 'answered') {
      throw new ManageFailure(r.state === 'expired'
        ? 'Keine Antwort vom Panel. Ist es online?'
        : 'Anfrage konnte nicht zugestellt werden.');
    }
    if (r.status !== null && r.status >= 400) {
      throw new ManageFailure((r.errorCode && ERRORS[r.errorCode]) || r.errorMessage || `Fehler ${r.status}`);
    }
    // Leseantworten kommen vom Client immer als Blob (ManageChannel: GET wird
    // nie eingebettet). Ohne diesen Abruf blieb der Dialog auf "Lade…"
    // stehen und zeigte Vorgaben statt der Werte des Panels (Kutiak, 18.10.2026).
    let payload: unknown = r.body;
    if ((payload === null || payload === undefined) && r.blobSha256
        && (r.contentType ?? 'application/json').includes('json')) {
      const res = await fetch(`/api/manage/requests/${sent.correlationId}/blob`, { credentials: 'include' });
      if (!res.ok) throw new ManageFailure(`Antwort des Panels nicht abrufbar (HTTP ${res.status}).`);
      payload = await res.json();
    }
    return { status: r.status ?? 200, body: payload as T, request: r };
  }
  throw new ManageFailure('Keine Antwort vom Panel innerhalb von 45 s.');
}

type Tab = 'updates' | 'anzeige' | 'kiosk' | 'netz' | 'vpn';

const TABS: { id: Tab; label: string }[] = [
  { id: 'updates', label: 'Updates' },
  { id: 'anzeige', label: 'Anzeige' },
  { id: 'kiosk', label: 'Kiosk und HMI' },
  { id: 'netz', label: 'Netz' },
  { id: 'vpn', label: 'VPN' },
];

export function PanelManageModal({ plantId, clientId, clientLabel, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('updates');
  const [keys, setKeys] = useState<ManageClientState | null>(null);
  const [panel, setPanel] = useState<{ info: Record<string, string> } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const run = <T,>(method: string, path: string, body?: unknown, reason?: string) =>
    send<T>(plantId, clientId, method, path, body, reason);

  useEffect(() => {
    api.get<ManageClientState>(`/api/plants/${plantId}/clients/${clientId}/manage`)
      .then(setKeys)
      .catch(() => setKeys(null));
    // Wer nur den Servicezugang darf (service.*), bekommt hier 403; das ist
    // kein Fehler des Geraets, also nicht als Ladefehler oben anzeigen.
    run<{ info: Record<string, string> }>('GET', '/v1/panel/settings')
      .then((a) => setPanel(a.body))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      });
  }, [plantId, clientId]);

  const hardening = panel?.info?.haertung_wirksam ?? null;
  // sinnux meldet seinen Bau; ab Bau 20261016103445 installiert eine Suche aus
  // HVACloud nur und startet nie neu, auch bei "sofort" (SINNUX, 16.10.2026).
  // Android-Panels spielen bei "sofort" weiterhin sofort ein.
  // Die laufende Fassung kommt aus ota-status (Name des Baus, der gerade
  // laeuft). Die Panel-Infos meldeten am 18.10.2026 nach dem Update auf 0.2.1
  // noch 0.2.0; sie sind nur der Rueckfall, bis der Update-Reiter geladen hat.
  const [runningVersion, setRunningVersion] = useState<string | null>(null);
  const ctx = { run, hardening, onRunningVersion: setRunningVersion };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal hv-modal--wide hv-panel-manage" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Touchpanel verwalten · {clientLabel}</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-panel-manage__status">
          {keys && !keys.deviceKeyPresent && (
            <span class="hv-panel-manage__warn">Das Panel hat noch keinen Verwaltungsschlüssel gemeldet.</span>
          )}
          {panel && (
            <>
              <span>sinnux <b class="mono">{runningVersion ?? panel.info.version ?? panel.info.fassung ?? '?'}</b></span>
              <span>Härtung <b>{hardening ?? '?'}</b></span>
              <span>Oberfläche {panel.info.app_laeuft === '1' ? 'läuft' : 'steht'}</span>
            </>
          )}
          {!panel && !loadError && <span class="muted">Frage das Panel ab…</span>}
          {loadError && <span class="hv-panel-manage__warn">{loadError}</span>}
        </div>
        <nav class="hv-panel-manage__tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                    class={`hv-panel-manage__tab${tab === t.id ? ' is-active' : ''}`}
                    onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </nav>
        <div class="hv-modal__body hv-panel-manage__body">
          {tab === 'updates' && <UpdatesTab {...ctx} />}
          {tab === 'anzeige' && <DisplayTab {...ctx} />}
          {tab === 'kiosk' && <KioskTab {...ctx} />}
          {tab === 'netz' && <NetTab {...ctx} />}
          {tab === 'vpn' && <VpnTab {...ctx} />}
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface TabProps {
  run<T>(method: string, path: string, body?: unknown, reason?: string): Promise<Answer<T>>;
  hardening: string | null;
  /** Meldet die Fassung, die laut ota-status gerade laeuft. */
  onRunningVersion?: (version: string | null) => void;
}

/** Laden beim Öffnen des Reiters plus ein Merker für laufende Aktionen. */
function useLoad<T>(run: TabProps['run'], path: string) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setError(null);
    run<T>('GET', path)
      .then((a) => live && setData(a.body))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => { live = false; };
  }, [path, tick]);
  return { data, setData, error, reload: () => setTick((t) => t + 1) };
}

function useBusy() {
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (label: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(label);
    try {
      await fn();
      if (ok) showToast({ tone: 'success', title: ok });
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht übernommen', message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };
  return { busy, act };
}

// ---------------------------------------------------------------- Updates ---

interface OtaStatus {
  installedVersion: string | null;
  name: string | null;
  channel: string | null;
  server: string | null;
  lastCheckEpoch: number | null;
  result: string | null;
  pendingVersion: string | null;
  restartWindow: string | null;
  restartAt: string | null;
  blockedVersion: string | null;
  /** Ab sinnux 0.2.1: bereit|pruefe|lade|installiere|wartet|startet-neu|fehler.
   *  Fehlt bei älteren Images; dann gilt die Anzeige aus result/pendingVersion.
   *  Den Grund bei "fehler" nennt result. */
  state?: string | null;
  target?: { build: string | null; version: string | null; client: string | null } | null;
  downloadedBytes?: number | null;
  totalBytes?: number | null;
}

interface OtaHistoryEntry {
  epoch: number;
  event: string;
  fromBuild: string | null;
  toBuild: string | null;
  origin: string | null;
}

/** Schritte der Leiste. Die Leiste folgt allein `state` des Panels, nie
 *  einer Vermutung der Oberfläche (Kutiak, 18.10.2026: fünfmal zeigte die
 *  Anzeige etwas anderes, als das Panel tat). */
const OTA_STEPS: { key: string; label: string }[] = [
  { key: 'pruefe', label: 'Suchen' },
  { key: 'lade', label: 'Laden' },
  { key: 'installiere', label: 'Installieren' },
  { key: 'wartet', label: 'Bereit zum Neustart' },
  { key: 'startet-neu', label: 'Neustart' },
  { key: 'fertig', label: 'Läuft' },
];

const OTA_STATE_TEXT: Record<string, string> = {
  bereit: 'Aktuell, nichts zu tun',
  pruefe: 'Sucht nach einem Update',
  lade: 'Lädt das Update',
  installiere: 'Installiert das Update',
  wartet: 'Update installiert, wartet auf den Neustart',
  'startet-neu': 'Startet neu',
  fehler: 'Fehler',
};

const OTA_EVENT_TEXT: Record<string, string> = {
  gefunden: 'Update gefunden',
  geladen: 'Update geladen',
  neustart: 'Neustart',
  laeuft: 'Läuft',
  zurueckgerollt: 'Zurückgerollt',
  fehlgeschlagen: 'Fehlgeschlagen',
};

/** Solange das Panel in einem dieser Zustände ist, fragt die Oberfläche nach. */
const OTA_ACTIVE = new Set(['pruefe', 'lade', 'installiere', 'startet-neu']);

function fmtBytes(n: number): string {
  return n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1e3)} kB`;
}

function fmtEpoch(e: number): string {
  return new Date(e * 1000).toLocaleString('de-AT');
}

/** Wohin die Leiste gerade zeigt. `watch` ist der Bau, auf den die Oberfläche
 *  nach "Einspielen" wartet; ist er installiert, steht die Leiste auf "Läuft". */
function otaStep(o: OtaStatus, watch: string | null, offline: boolean, done: boolean): string | null {
  if (done) return 'fertig';
  if (watch && o.installedVersion === watch && !offline) return 'fertig';
  // Nach "Einspielen" steht sofort "Neustart", auch wenn das Panel (alter
  // Client) noch keinen Zustand meldet: der Auftrag ist ja angenommen.
  if (watch) return 'startet-neu';
  const s = o.state;
  if (!s || s === 'bereit' || s === 'fehler') return null;
  return s;
}

function OtaStepper({ step, failed }: { step: string | null; failed: boolean }) {
  const at = step ? OTA_STEPS.findIndex((x) => x.key === step) : -1;
  // "Läuft" ist kein laufender Schritt, sondern das Ende: dann alles erledigt.
  const finished = step === 'fertig';
  return (
    <ol class="hv-ota-steps" aria-label="Ablauf des Updates">
      {OTA_STEPS.map((x, i) => {
        const cls = finished || i < at ? 'is-done' : i === at ? (failed ? 'is-failed' : 'is-current') : '';
        return <li key={x.key} class={cls} aria-current={i === at ? 'step' : undefined}>{x.label}</li>;
      })}
    </ol>
  );
}

function OtaHistoryList({ run, tick }: { run: TabProps['run']; tick: number }) {
  const [rows, setRows] = useState<OtaHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    run<{ entries: OtaHistoryEntry[] }>('GET', '/v1/ota/history')
      .then((a) => { if (live) { setRows(a.body.entries ?? []); setError(null); } })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => { live = false; };
  }, [tick]);
  if (error) return <p class="muted">Den Verlauf meldet das Panel ab sinnux 0.2.1 mit Client 20261018-5d70f72. ({error})</p>;
  if (!rows) return <p class="muted">Lade…</p>;
  if (rows.length === 0) return <p class="muted">Noch keine Updates verzeichnet.</p>;
  return (
    <table class="hv-ota-history">
      <thead><tr><th>Zeit</th><th>Ereignis</th><th>Bau</th><th>Auslöser</th></tr></thead>
      <tbody>
        {[...rows].reverse().map((r) => (
          <tr key={`${r.epoch}-${r.event}`}>
            <td>{fmtEpoch(r.epoch)}</td>
            <td class={r.event === 'fehlgeschlagen' || r.event === 'zurueckgerollt' ? 'hv-panel-manage__warn' : ''}>
              {OTA_EVENT_TEXT[r.event] ?? r.event}
            </td>
            <td class="mono">{r.fromBuild && r.toBuild ? `${r.fromBuild} → ${r.toBuild}` : r.toBuild ?? r.fromBuild ?? '–'}</td>
            <td>{r.origin === 'manuell' ? 'von Hand' : r.origin === 'timer' ? 'automatisch' : r.origin ?? '–'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UpdatesTab({ run, onRunningVersion }: TabProps) {
  const s = useLoad<OtaStatus>(run, '/v1/ota/status');
  useEffect(() => { if (s.data?.name) onRunningVersion?.(s.data.name); }, [s.data?.name]);
  const [checking, setChecking] = useState<string | null>(null);
  const { busy, act } = useBusy();
  const [channel, setChannel] = useState('');
  const [windowMode, setWindowMode] = useState<'sofort' | 'fenster'>('fenster');
  const [from, setFrom] = useState('02:00');
  const [to, setTo] = useState('04:00');
  // Nach "Einspielen": der Bau, auf den gewartet wird, und ob das Panel gerade
  // nicht antwortet. Die Oberfläche bleibt dran, bis er installiert ist.
  const [watch, setWatch] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [historyTick, setHistoryTick] = useState(0);

  useEffect(() => {
    if (!s.data) return;
    setChannel(s.data.channel ?? 'stabil');
    const w = s.data.restartWindow ?? '';
    if (w === 'sofort') setWindowMode('sofort');
    else if (/^\d\d:\d\d-\d\d:\d\d$/.test(w)) {
      setWindowMode('fenster');
      setFrom(w.slice(0, 5));
      setTo(w.slice(6));
    }
  }, [s.data]);

  // Dranbleiben: solange das Panel arbeitet oder ein Einspielen läuft, alle
  // 5 s nachfragen. Antwortet es nicht, startet es vermutlich neu; nach
  // höchstens 10 min ohne Ergebnis hört die Oberfläche auf und sagt das.
  const active = !!watch || OTA_ACTIVE.has(s.data?.state ?? '');
  useEffect(() => {
    if (!active) return;
    let live = true;
    const started = Date.now();
    const timer = window.setInterval(async () => {
      if (!live) return;
      if (Date.now() - started > 10 * 60_000) {
        window.clearInterval(timer);
        if (watch) setChecking(`Nach 10 Minuten läuft ${watch} noch nicht. Bitte am Panel nachsehen.`);
        setWatch(null);
        return;
      }
      try {
        const b = (await run<OtaStatus>('GET', '/v1/ota/status')).body;
        if (!live) return;
        setOffline(false);
        s.setData(b);
        if (watch && b.installedVersion === watch) {
          setSuccess(`Update erfolgreich: ${watch} läuft.`);
          setWatch(null);
          setChecking(null);
          setHistoryTick((t) => t + 1);
        } else if (watch && b.state === 'fehler') {
          setChecking(null);
          setWatch(null);
          setHistoryTick((t) => t + 1);
        } else if (!watch && !OTA_ACTIVE.has(b.state ?? '')) {
          setHistoryTick((t) => t + 1);
        }
      } catch {
        if (live) setOffline(true);
      }
    }, 5000);
    return () => { live = false; window.clearInterval(timer); };
  }, [active, watch]);

  const o = s.data;
  const modern = !!o?.state;
  // Ab Bau 20261016103445 installiert eine Suche aus HVACloud nur und startet
  // nie neu, auch bei "sofort" (SINNUX, 16.10.2026). Den Bau nennt ota-status;
  // sinnux 0.2.0 meldet ihn nicht mehr in den Panel-Infos (18.10.2026).
  const searchOnly = /^\d{14}$/.test(o?.installedVersion ?? '') && (o?.installedVersion ?? '') >= '20261016103445';
  // Was eingespielt würde: ab 0.2.1 aus target, sonst aus "ausstehend". Nach
  // dem Neustart steht der eingespielte Bau noch bis zu 15 min als
  // "ausstehend", bis der Timer aufräumt (sinnux, 16.10.2026).
  const legacyPending = o?.pendingVersion && o.pendingVersion !== o.installedVersion ? o.pendingVersion : null;
  const pending = modern ? (o?.state === 'wartet' ? (o.target?.build ?? legacyPending) : null) : legacyPending;
  const failed = o?.state === 'fehler';
  const step = o ? otaStep(o, watch, offline, !!success) : null;
  const progress = o?.state === 'lade' && o.totalBytes ? Math.min(1, (o.downloadedBytes ?? 0) / o.totalBytes) : null;
  return (
    <div class="hv-panel-manage__grid">
      <section class="hv-panel-manage__card hv-ota">
        <h3>Stand</h3>
        {s.error && <p class="hv-panel-manage__warn">{s.error}</p>}
        {!o && !s.error && <p class="muted">Lade…</p>}
        {o && (modern || watch || success) && (
          <>
            <OtaStepper step={step} failed={failed} />
            <p class={failed ? 'hv-panel-manage__warn' : 'hv-ota__now'} role="status">
              {offline && watch ? 'Panel antwortet nicht, es startet neu. Die Anzeige bleibt dran.'
                : watch ? `${watch} wird eingespielt, das Panel startet gleich neu.`
                : failed ? `Fehler: ${o.result ?? 'ohne Angabe'}`
                : success ? 'Fertig'
                : OTA_STATE_TEXT[o.state ?? ''] ?? o.state ?? ''}
            </p>
            {progress !== null && (
              <div class="hv-ota__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
                <span style={{ width: `${progress * 100}%` }} />
                <em>{fmtBytes(o.downloadedBytes ?? 0)} von {fmtBytes(o.totalBytes!)}</em>
              </div>
            )}
            {success && <p class="hv-ota__ok" role="status">{success}</p>}
          </>
        )}
        {o && (
          <dl class="hv-panel-manage__kv">
            <dt>Installiert</dt><dd class="mono">{o.installedVersion ?? '–'}</dd>
            <dt>Zuletzt geprüft</dt><dd>{o.lastCheckEpoch ? fmtEpoch(o.lastCheckEpoch) : 'noch nie'}</dd>
            {!modern && (<><dt>Ergebnis</dt><dd>{o.result ?? '–'}</dd></>)}
            {modern && o.target && o.state !== 'bereit' && (
              <>
                <dt>Was kommt</dt>
                <dd>
                  <span class="mono">{o.target.build ?? '–'}</span>
                  {o.target.version && <> · sinnux {o.target.version}</>}
                  {o.target.client && <> · Client <span class="mono">{o.target.client}</span></>}
                </dd>
              </>
            )}
            {!modern && (
              <>
                <dt>Bereit zum Einspielen</dt>
                <dd>{pending ? <b class="mono">{pending}</b> : <span class="muted">nichts</span>}</dd>
              </>
            )}
            {o.restartAt && (<><dt>Neustart geplant</dt><dd>{o.restartAt}</dd></>)}
            {o.blockedVersion && (<><dt>Gesperrt</dt><dd class="mono">{o.blockedVersion}</dd></>)}
          </dl>
        )}
        {o && !modern && !watch && !success && <p class="muted">Dieses Panel meldet noch keinen Ablauf. Die Schrittleiste braucht sinnux 0.2.1 mit Client 20261018-5d70f72 oder neuer.</p>}
        {checking && <p class="hv-panel-manage__warn" role="status">{checking}</p>}
        <div class="hv-panel-manage__actions">
          <button type="button" class="hv-btn" disabled={!!busy || !o || active}
                  title={o ? '' : 'Erst muss der Stand des Panels geladen sein.'}
                  onClick={() => {
                    // Bei Neustart "sofort" spielt das Panel ein gefundenes Update
                    // ohne weitere Rueckfrage ein und startet neu. Am 16.10.2026
                    // war der Stand noch nicht geladen, die Rueckfrage fiel aus und
                    // ein Klick auf "Suchen" startete das Panel neu (Patrick).
                    if (!o) return;
                    if (!searchOnly && (o.restartWindow === 'sofort' || windowMode === 'sofort') && !confirm(
                      'Achtung: Das Panel steht auf Neustart "sofort". Die Suche spielt ein gefundenes Update '
                      + 'SOFORT ein und startet das Panel neu, etwa 2 Minuten nicht bedienbar. '
                      + 'Nur suchen geht in dieser Einstellung nicht. Trotzdem suchen und einspielen?')) return;
                    setSuccess(null);
                    void act('check', async () => {
                    const a = await run<OtaStatus>('POST', '/v1/ota/check', {}, 'Update-Suche aus HVACloud');
                    const start = a.body;
                    s.setData(start);
                    if (start.state) return; // ab 0.2.1 bleibt die Leiste selbst dran
                    // Aeltere Images: bis zu 2 min alle 5 s nachsehen, bis sich etwas aendert.
                    setChecking('Suche läuft am Panel …');
                    for (let i = 0; i < 24; i++) {
                      await new Promise((r) => setTimeout(r, 5000));
                      try {
                        const b = (await run<OtaStatus>('GET', '/v1/ota/status')).body;
                        s.setData(b);
                        if (b.lastCheckEpoch !== start.lastCheckEpoch || b.result !== start.result
                            || b.pendingVersion !== start.pendingVersion || b.installedVersion !== start.installedVersion) {
                          setChecking(null);
                          return;
                        }
                      } catch {
                        setChecking('Panel antwortet nicht, es startet vermutlich gerade neu (Update wird eingespielt).');
                      }
                    }
                    setChecking(null);
                  }, 'Update-Suche gestartet.');
                  }}>
            {busy === 'check' ? 'Suche…' : !searchOnly && o?.restartWindow === 'sofort' ? 'Suchen und sofort einspielen' : 'Jetzt nach Update suchen'}
          </button>
          <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || !pending || !!watch}
                  title={pending ? '' : 'Erst muss ein Update geladen und installiert sein.'}
                  onClick={() => {
                    const t = modern ? o?.target : null;
                    const extra = [t?.version ? `sinnux ${t.version}` : null, t?.client ? `Client ${t.client}` : null].filter(Boolean).join(', ');
                    const what = extra ? `${pending} (${extra})` : pending;
                    if (!confirm(`Update ${what} jetzt einspielen? Das Panel startet neu und ist etwa zwei Minuten nicht erreichbar. Die Anzeige bleibt dran, bis es wieder läuft.`)) return;
                    setSuccess(null);
                    act('apply', async () => {
                      await run('POST', '/v1/ota/apply', {}, 'Update aus HVACloud eingespielt');
                      setWatch(pending);
                    }, 'Update wird eingespielt. Das Panel startet neu.');
                  }}>
            {busy === 'apply' ? 'Spiele ein…' : watch ? 'Wird eingespielt…' : 'Jetzt einspielen und neu starten'}
          </button>
        </div>
      </section>

      <section class="hv-panel-manage__card">
        <h3>Verlauf</h3>
        <OtaHistoryList run={run} tick={historyTick} />
      </section>

      <section class="hv-panel-manage__card">
        <h3>Einstellungen</h3>
        <label class="hv-modal__field">
          <span>Kanal</span>
          <select value={channel} disabled={!o} onChange={(e) => setChannel((e.target as HTMLSelectElement).value)}>
            <option value="stabil">stabil</option>
            <option value="experimentell">experimentell</option>
          </select>
        </label>
        <label class="hv-modal__field">
          <span>Neustart nach Update</span>
          <select value={windowMode} disabled={!o} onChange={(e) => setWindowMode((e.target as HTMLSelectElement).value as 'sofort' | 'fenster')}>
            <option value="fenster">im Zeitfenster</option>
            <option value="sofort">sofort</option>
          </select>
        </label>
        {windowMode === 'sofort' && (
          <p class="hv-panel-manage__warn">
            Achtung: Das Panel startet bei jedem Update sofort und ohne Rückfrage neu, auch beim Kunden im Betrieb.
            Für Anlagen im Feld besser ein Zeitfenster in der Nacht.
          </p>
        )}
        {windowMode === 'fenster' && (
          <div class="hv-panel-manage__row">
            <label class="hv-modal__field"><span>von</span>
              <input type="time" value={from} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} /></label>
            <label class="hv-modal__field"><span>bis</span>
              <input type="time" value={to} onInput={(e) => setTo((e.target as HTMLInputElement).value)} /></label>
          </div>
        )}
        <p class="hv-modal__hint">Update-Server: <span class="mono">{o?.server ?? 'Voreinstellung des Image'}</span></p>
        <div class="hv-panel-manage__actions">
          {/* Erst speichern, wenn die Werte des Panels geladen sind: sonst
              schreibt ein Klick die Vorgaben (02:00 bis 04:00) ueber die
              echten Einstellungen (Kutiak, 18.10.2026). */}
          <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || !o}
                  title={o ? '' : 'Erst müssen die Einstellungen des Panels geladen sein.'}
                  onClick={() => act('save', async () => {
                    await run('POST', '/v1/ota/settings', {
                      channel,
                      restartWindow: windowMode === 'sofort' ? 'sofort' : `${from}-${to}`,
                    }, 'Update-Einstellungen aus HVACloud');
                    s.reload();
                  }, 'Gespeichert.')}>
            {busy === 'save' ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- Anzeige ---

function DisplayTab({ run }: TabProps) {
  const d = useLoad<{
    brightness: number | null; rotation: number;
    screenOffMinutes?: number; screenDark?: boolean; screenOffSupported?: boolean;
  }>(run, '/v1/display/settings');
  const tz = useLoad<{ timezone: string | null }>(run, '/v1/system/timezone');
  const { busy, act } = useBusy();
  const [brightness, setBrightness] = useState(200);
  const [shot, setShot] = useState<string | null>(null);
  const brightTimer = useRef<number | null>(null);
  const [zone, setZone] = useState('');
  const [offMinutes, setOffMinutes] = useState('0');

  useEffect(() => { if (d.data?.brightness) setBrightness(d.data.brightness); }, [d.data]);
  useEffect(() => { setZone(tz.data?.timezone ?? 'Europe/Vienna'); }, [tz.data]);
  useEffect(() => { setOffMinutes(String(d.data?.screenOffMinutes ?? 0)); }, [d.data]);
  const offValue = Number(offMinutes);
  const offValid = offMinutes.trim() !== '' && Number.isInteger(offValue) && offValue >= 0 && offValue <= 1440;

  const screenshot = () => act('shot', async () => {
    const a = await run('GET', '/v1/display/screenshot');
    const sha = a.request.blobSha256;
    if (!sha) throw new Error('Das Panel hat kein Bild geschickt.');
    // Das Bild kommt als Blob nach; kurz warten, bis es hochgeladen ist.
    for (let i = 0; i < 20; i++) {
      const res = await fetch(`/api/manage/requests/${a.request.correlationId}/blob`, { credentials: 'include' });
      if (res.ok) {
        if (shot) URL.revokeObjectURL(shot);
        setShot(URL.createObjectURL(await res.blob()));
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
    throw new Error('Bild ist nicht angekommen.');
  });

  return (
    <div class="hv-panel-manage__grid">
      <section class="hv-panel-manage__card">
        <h3>Bildschirm</h3>
        {d.error && <p class="hv-panel-manage__warn">{d.error}</p>}
        <label class="hv-modal__field">
          <span>Helligkeit {Math.round((brightness / 255) * 100)} %</span>
          <input type="range" min={10} max={255} value={brightness}
                 onInput={(e) => {
                   // Erst senden, wenn der Regler 0,4 s ruht: am 16.10.2026 gingen
                   // beim Ziehen 29 Anfragen in einer Sekunde ans Panel, 25 abgewiesen.
                   const v = Number((e.target as HTMLInputElement).value);
                   setBrightness(v);
                   if (brightTimer.current) window.clearTimeout(brightTimer.current);
                   brightTimer.current = window.setTimeout(() => {
                     act('bright', () => run('POST', '/v1/display/backlight', { brightness: v }, 'Helligkeit aus HVACloud'));
                   }, 400);
                 }} />
        </label>
        <div class="hv-modal__field">
          <span>Drehung</span>
          <div class="hv-panel-manage__seg">
            {[0, 90, 180, 270].map((r) => (
              <button key={r} type="button" disabled={!!busy}
                      class={`hv-btn hv-btn--sm${d.data?.rotation === r ? ' hv-btn--primary' : ''}`}
                      onClick={() => act('rot', async () => {
                        await run('POST', '/v1/display/orientation', { rotation: r }, 'Drehung aus HVACloud');
                        d.setData({ ...d.data!, brightness, rotation: r });
                      }, `Auf ${r}° gedreht.`)}>{r}°</button>
            ))}
          </div>
        </div>
        {/* Nur wo das Image es kann: aeltere sinnux-Baue lehnen DUNKEL_NACH ab
            (SINNUX, 18.10.2026). Nur das Licht geht aus; die erste Beruehrung
            macht hell und erreicht die HMI nicht. */}
        {d.data?.screenOffSupported && (
          <label class="hv-modal__field">
            <span>Bildschirm dunkel nach (Minuten){d.data.screenDark ? ' · gerade dunkel' : ''}</span>
            <div class="hv-panel-manage__row">
              <input type="number" min={0} max={1440} step={1} value={offMinutes}
                     onInput={(e) => setOffMinutes((e.target as HTMLInputElement).value)} />
              <button type="button" class="hv-btn" disabled={!!busy || !offValid || offValue === (d.data.screenOffMinutes ?? 0)}
                      onClick={() => act('dark', async () => {
                        await run('POST', '/v1/display/timeout', { screenOffMinutes: offValue }, 'Bildschirm dunkel aus HVACloud');
                        d.setData({ ...d.data!, screenOffMinutes: offValue, screenDark: false });
                      }, offValue === 0 ? 'Bildschirm bleibt immer hell.' : `Dunkel nach ${offValue} min ohne Berührung.`)}>
                Setzen
              </button>
            </div>
            <span class="hv-modal__hint">
              {offValid ? (offValue === 0 ? '0 heißt nie.' : 'Nur das Licht geht aus, die erste Berührung macht hell und löst in der HMI nichts aus.')
                : 'Ganze Minuten von 0 bis 1440.'}
            </span>
          </label>
        )}
        <label class="hv-modal__field">
          <span>Zeitzone</span>
          <div class="hv-panel-manage__row">
            <input value={zone} onInput={(e) => setZone((e.target as HTMLInputElement).value)} placeholder="Europe/Vienna" />
            <button type="button" class="hv-btn" disabled={!!busy || !tz.data}
                    onClick={() => act('tz', () => run('POST', '/v1/system/timezone', { timezone: zone }, 'Zeitzone aus HVACloud'), 'Zeitzone gesetzt.')}>
              Setzen
            </button>
          </div>
        </label>
      </section>
      <section class="hv-panel-manage__card">
        <h3>Bildschirmbild</h3>
        <p class="hv-modal__hint">Zeigt, was gerade am Panel zu sehen ist.</p>
        <button type="button" class="hv-btn" disabled={!!busy} onClick={screenshot}>
          {busy === 'shot' ? 'Hole Bild…' : 'Bild holen'}
        </button>
        {shot && <img class="hv-panel-manage__shot" src={shot} alt="Bildschirm des Panels" />}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------ Kiosk ---

interface HmiState {
  app: string | null;
  url: string | null;
  basicAuthUser: string | null;
  basicAuthPasswordSet: boolean;
  running: boolean;
}

function KioskTab({ run }: TabProps) {
  const h = useLoad<HmiState>(run, '/v1/hmi');
  const { busy, act } = useBusy();
  const [app, setApp] = useState('browser');
  const [url, setUrl] = useState('');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');

  useEffect(() => {
    if (!h.data) return;
    setApp(h.data.app ?? 'browser');
    setUrl(h.data.url ?? '');
    setUser(h.data.basicAuthUser ?? '');
  }, [h.data]);

  return (
    <section class="hv-panel-manage__card">
      <h3>Was das Panel anzeigt</h3>
      {h.error && <p class="hv-panel-manage__warn">{h.error}</p>}
      <div class="hv-modal__radio-list">
        <label class="hv-modal__radio-row">
          <input type="radio" checked={app === 'browser'} onChange={() => setApp('browser')} />
          <span>Kiosk-Browser mit fester Adresse</span>
        </label>
        <label class="hv-modal__radio-row">
          <input type="radio" checked={app === 'hmi'} onChange={() => setApp('hmi')} />
          <span>HVACloud-HMI des Panels</span>
        </label>
      </div>
      {app === 'browser' && (
        <>
          <label class="hv-modal__field"><span>Adresse</span>
            <input value={url} placeholder="http://127.0.0.1:8480/hmi/"
                   onInput={(e) => setUrl((e.target as HTMLInputElement).value)} /></label>
          <div class="hv-panel-manage__row">
            <label class="hv-modal__field"><span>Benutzer (optional)</span>
              <input value={user} onInput={(e) => setUser((e.target as HTMLInputElement).value)} /></label>
            <label class="hv-modal__field">
              <span>Kennwort {h.data?.basicAuthPasswordSet ? '(hinterlegt, leer lassen = bleibt)' : '(optional)'}</span>
              <input type="password" autocomplete="new-password" value={pass}
                     onInput={(e) => setPass((e.target as HTMLInputElement).value)} /></label>
          </div>
        </>
      )}
      <div class="hv-panel-manage__actions">
        <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || !h.data}
                onClick={() => act('hmi', async () => {
                  const body: Record<string, string> = { app };
                  if (app === 'browser') {
                    body.url = url;
                    body.basicAuthUser = user;
                    if (pass) body.basicAuthPassword = pass;
                  }
                  await run('POST', '/v1/hmi', body, 'Kiosk aus HVACloud');
                  setPass('');
                  h.reload();
                }, 'Übernommen. Das Panel lädt neu.')}>
          {busy === 'hmi' ? 'Übernehme…' : 'Übernehmen'}
        </button>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------- Netz ---

interface Eth { mode: string; ip: string | null; prefixLength: number | null; gateway: string | null; dns: string[]; carrier: boolean }
interface Eth2 { ip: string | null; prefixLength: number | null; dhcp: { from: string; to: string } | null; carrier: boolean }
interface Wifi {
  enabled: boolean; ssid: string | null; security: string | null; pskSet: boolean; country: string | null;
  connectedAddress: string | null; signal: number | null;
}

/** Netzänderung absetzen und bis zur Bestätigung durch den Server mitverfolgen. */
async function netChange(run: TabProps['run'], path: string, body: unknown, reason: string): Promise<string> {
  const a = await run<{ confirmToken?: string; revertsAtEpochMs?: number }>('POST', path, body, reason);
  if (!a.body?.confirmToken) return 'Übernommen.';
  // Der Server bestätigt selbst, sobald das Panel wieder signiert antwortet.
  const until = Date.now() + 150_000;
  while (Date.now() < until) {
    await new Promise((r) => setTimeout(r, 3000));
    const r = await api.get<ManageRequestDto>(`/api/manage/requests/${a.request.correlationId}`);
    if (r.netChangeState === 'confirmed') return 'Übernommen und bestätigt. Das Panel ist mit den neuen Einstellungen erreichbar.';
    if (r.netChangeState === 'left_to_revert' || r.netChangeState === 'confirm_rejected') {
      throw new Error('Das Panel war danach nicht erreichbar und hat die alten Einstellungen zurückgeholt.');
    }
  }
  throw new Error('Keine Bestätigung. Das Panel nimmt die Änderung nach 120 s selbst zurück.');
}

function NetTab({ run, hardening }: TabProps) {
  const e1 = useLoad<Eth>(run, '/v1/net/ethernet');
  const e2 = useLoad<Eth2>(run, '/v1/net/ethernet2');
  const w = useLoad<Wifi>(run, '/v1/net/wifi');
  const { busy, act } = useBusy();
  const locked = hardening !== null && hardening !== 'werkstatt';

  const [lan1, setLan1] = useState({ mode: 'dhcp', ip: '', prefix: '24', gw: '', dns: '' });
  const [lan2, setLan2] = useState({ ip: '', prefix: '24', from: '', to: '' });
  const [wifi, setWifi] = useState({ enabled: false, ssid: '', security: 'wpa2', psk: '', country: 'AT' });
  const [scan, setScan] = useState<{ ssid: string; signal: number; sicherheit?: string }[] | null>(null);

  useEffect(() => {
    if (e1.data) setLan1({ mode: e1.data.mode, ip: e1.data.ip ?? '', prefix: String(e1.data.prefixLength ?? 24),
                           gw: e1.data.gateway ?? '', dns: (e1.data.dns ?? []).join(' ') });
  }, [e1.data]);
  useEffect(() => {
    if (e2.data) setLan2({ ip: e2.data.ip ?? '', prefix: String(e2.data.prefixLength ?? 24),
                           from: e2.data.dhcp?.from ?? '', to: e2.data.dhcp?.to ?? '' });
  }, [e2.data]);
  useEffect(() => {
    if (w.data) setWifi({ enabled: w.data.enabled, ssid: w.data.ssid ?? '', security: w.data.security ?? 'wpa2',
                          psk: '', country: w.data.country ?? 'AT' });
  }, [w.data]);

  const confirmText = 'Das Panel stellt um und wartet 120 s auf eine Bestätigung von HVACloud. '
    + 'Kommt keine, holt es die alten Einstellungen zurück. Fortfahren?';

  return (
    <>
      {locked && (
        <p class="hv-panel-manage__warn">
          Härtung steht auf „{hardening}“. Netzänderungen gehen nur in „werkstatt“; die Stufe stellt man am Panel selbst um.
        </p>
      )}
      <div class="hv-panel-manage__grid">
        <section class="hv-panel-manage__card">
          <h3>LAN 1 <small class="muted">{e1.data ? (e1.data.carrier ? '· Kabel steckt' : '· kein Kabel') : ''}</small></h3>
          {e1.error && <p class="hv-panel-manage__warn">{e1.error}</p>}
          <label class="hv-modal__field"><span>Adresse beziehen</span>
            <select value={lan1.mode} onChange={(e) => setLan1({ ...lan1, mode: (e.target as HTMLSelectElement).value })}>
              <option value="dhcp">automatisch (DHCP)</option>
              <option value="static">fest</option>
            </select></label>
          {lan1.mode === 'static' ? (
            <>
              <div class="hv-panel-manage__row">
                <label class="hv-modal__field"><span>IP-Adresse</span>
                  <input value={lan1.ip} onInput={(e) => setLan1({ ...lan1, ip: (e.target as HTMLInputElement).value })} /></label>
                <label class="hv-modal__field hv-panel-manage__narrow"><span>Präfix</span>
                  <input value={lan1.prefix} onInput={(e) => setLan1({ ...lan1, prefix: (e.target as HTMLInputElement).value })} /></label>
              </div>
              <label class="hv-modal__field"><span>Gateway</span>
                <input value={lan1.gw} onInput={(e) => setLan1({ ...lan1, gw: (e.target as HTMLInputElement).value })} /></label>
              <label class="hv-modal__field"><span>DNS (mit Leerzeichen getrennt)</span>
                <input value={lan1.dns} onInput={(e) => setLan1({ ...lan1, dns: (e.target as HTMLInputElement).value })} /></label>
            </>
          ) : (
            e1.data?.ip && <p class="hv-modal__hint">Derzeit <span class="mono">{e1.data.ip}/{e1.data.prefixLength}</span>, Gateway <span class="mono">{e1.data.gateway ?? '–'}</span></p>
          )}
          <div class="hv-panel-manage__actions">
            <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || locked || !e1.data}
                    onClick={() => confirm(confirmText) && act('lan1', () => netChange(run, '/v1/net/ethernet',
                      lan1.mode === 'dhcp' ? { mode: 'dhcp' } : {
                        mode: 'static', ip: lan1.ip, prefixLength: Number(lan1.prefix), gateway: lan1.gw,
                        dns: lan1.dns.split(/\s+/).filter(Boolean),
                      }, 'LAN 1 aus HVACloud').then((m) => { showToast({ tone: 'success', title: m }); e1.reload(); }))}>
              {busy === 'lan1' ? 'Stelle um, warte auf Bestätigung…' : 'Übernehmen'}
            </button>
          </div>
        </section>

        <section class="hv-panel-manage__card">
          <h3>LAN 2 (Anlagennetz) <small class="muted">{e2.data ? (e2.data.carrier ? '· Kabel steckt' : '· kein Kabel') : ''}</small></h3>
          {e2.error && <p class="hv-panel-manage__warn">{e2.error}</p>}
          <div class="hv-panel-manage__row">
            <label class="hv-modal__field"><span>IP-Adresse des Panels</span>
              <input value={lan2.ip} onInput={(e) => setLan2({ ...lan2, ip: (e.target as HTMLInputElement).value })} /></label>
            <label class="hv-modal__field hv-panel-manage__narrow"><span>Präfix</span>
              <input value={lan2.prefix} onInput={(e) => setLan2({ ...lan2, prefix: (e.target as HTMLInputElement).value })} /></label>
          </div>
          <div class="hv-panel-manage__row">
            <label class="hv-modal__field"><span>DHCP von</span>
              <input value={lan2.from} placeholder="leer = kein DHCP" onInput={(e) => setLan2({ ...lan2, from: (e.target as HTMLInputElement).value })} /></label>
            <label class="hv-modal__field"><span>bis</span>
              <input value={lan2.to} onInput={(e) => setLan2({ ...lan2, to: (e.target as HTMLInputElement).value })} /></label>
          </div>
          <div class="hv-panel-manage__actions">
            <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || locked || !e2.data}
                    onClick={() => confirm(confirmText) && act('lan2', () => netChange(run, '/v1/net/ethernet2', {
                      ip: lan2.ip, prefixLength: Number(lan2.prefix),
                      dhcp: lan2.from && lan2.to ? { from: lan2.from, to: lan2.to } : null,
                    }, 'LAN 2 aus HVACloud').then((m) => { showToast({ tone: 'success', title: m }); e2.reload(); }))}>
              {busy === 'lan2' ? 'Stelle um…' : 'Übernehmen'}
            </button>
          </div>
        </section>

        <section class="hv-panel-manage__card">
          <h3>WLAN {w.data?.connectedAddress && <small class="muted">· verbunden als {w.data.connectedAddress}{w.data.signal ? `, ${w.data.signal} dBm` : ''}</small>}</h3>
          {w.error && <p class="hv-panel-manage__warn">{w.error}</p>}
          <label class="hv-modal__radio-row">
            <input type="checkbox" checked={wifi.enabled} onChange={(e) => setWifi({ ...wifi, enabled: (e.target as HTMLInputElement).checked })} />
            <span>WLAN verwenden</span>
          </label>
          {wifi.enabled && (
            <>
              <div class="hv-panel-manage__row">
                <label class="hv-modal__field"><span>Netz (SSID)</span>
                  <input value={wifi.ssid} list="hv-panel-ssids" onInput={(e) => setWifi({ ...wifi, ssid: (e.target as HTMLInputElement).value })} /></label>
                <button type="button" class="hv-btn hv-btn--sm" disabled={!!busy}
                        onClick={() => act('scan', async () => {
                          const a = await run<{ networks: { ssid: string; signal: number; sicherheit?: string }[] }>('GET', '/v1/net/wifi/scan');
                          setScan(a.body.networks.filter((n) => n.ssid));
                        })}>{busy === 'scan' ? 'Suche…' : 'Netze suchen'}</button>
              </div>
              {scan && (
                <datalist id="hv-panel-ssids">
                  {scan.map((n) => <option key={n.ssid} value={n.ssid}>{n.signal} dBm</option>)}
                </datalist>
              )}
              {scan && <p class="hv-modal__hint">{scan.length} Netze gefunden, Auswahl im Feld.</p>}
              <div class="hv-panel-manage__row">
                <label class="hv-modal__field"><span>Sicherheit</span>
                  <select value={wifi.security} onChange={(e) => setWifi({ ...wifi, security: (e.target as HTMLSelectElement).value })}>
                    <option value="wpa2">WPA2</option><option value="wpa3">WPA3</option><option value="offen">offen</option>
                  </select></label>
                <label class="hv-modal__field hv-panel-manage__narrow"><span>Land</span>
                  <input value={wifi.country} onInput={(e) => setWifi({ ...wifi, country: (e.target as HTMLInputElement).value })} /></label>
              </div>
              {wifi.security !== 'offen' && (
                <label class="hv-modal__field">
                  <span>Kennwort {w.data?.pskSet ? '(hinterlegt, leer lassen = bleibt)' : ''}</span>
                  <input type="password" autocomplete="new-password" value={wifi.psk}
                         onInput={(e) => setWifi({ ...wifi, psk: (e.target as HTMLInputElement).value })} /></label>
              )}
            </>
          )}
          <div class="hv-panel-manage__actions">
            <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || locked || !w.data}
                    onClick={() => confirm(confirmText) && act('wifi', () => netChange(run, '/v1/net/wifi',
                      wifi.enabled ? {
                        enabled: true, ssid: wifi.ssid, security: wifi.security, country: wifi.country,
                        ...(wifi.psk ? { psk: wifi.psk } : {}),
                      } : { enabled: false }, 'WLAN aus HVACloud').then((m) => {
                        setWifi({ ...wifi, psk: '' });
                        showToast({ tone: 'success', title: m });
                        w.reload();
                      }))}>
              {busy === 'wifi' ? 'Stelle um…' : 'Übernehmen'}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}

// -------------------------------------------------------------------- VPN ---

function VpnTab({ run, hardening }: TabProps) {
  const v = useLoad<{ status: string }>(run, '/v1/net/vpn');
  const { busy, act } = useBusy();
  const [profile, setProfile] = useState<string | null>(null);
  const locked = hardening !== null && hardening !== 'werkstatt';

  const confirmed = (path: string, body: unknown, reason: string) =>
    netChange(run, path, body, reason).then((m) => { showToast({ tone: 'success', title: m }); v.reload(); });

  return (
    <div class="hv-panel-manage__grid">
      <section class="hv-panel-manage__card">
        <h3>Zustand</h3>
        {v.error && <p class="hv-panel-manage__warn">{v.error}</p>}
        <pre class="hv-panel-manage__pre">{v.data?.status || (v.error ? '' : 'Lade…')}</pre>
        <div class="hv-panel-manage__actions">
          <button type="button" class="hv-btn" disabled={!!busy || locked}
                  onClick={() => act('up', () => confirmed('/v1/net/vpn/up', {}, 'VPN ein aus HVACloud'))}>
            {busy === 'up' ? 'Verbinde…' : 'VPN einschalten'}
          </button>
          <button type="button" class="hv-btn" disabled={!!busy || locked}
                  onClick={() => confirm('VPN ausschalten? Wer gerade über das VPN auf das Panel zugreift, verliert die Verbindung.')
                    && act('down', () => confirmed('/v1/net/vpn/down', {}, 'VPN aus aus HVACloud'))}>
            {busy === 'down' ? 'Trenne…' : 'VPN ausschalten'}
          </button>
        </div>
      </section>
      <section class="hv-panel-manage__card">
        <h3>Profil hochladen</h3>
        <p class="hv-modal__hint">OpenVPN-Profil (.ovpn, höchstens 64 KB). Ersetzt das bisherige.</p>
        <input type="file" accept=".ovpn,.conf,text/plain"
               onChange={async (e) => {
                 const f = (e.target as HTMLInputElement).files?.[0];
                 setProfile(f ? await f.text() : null);
               }} />
        <div class="hv-panel-manage__actions">
          <button type="button" class="hv-btn hv-btn--primary" disabled={!!busy || locked || !profile}
                  onClick={() => act('profile', () => confirmed('/v1/net/vpn/profile',
                    { profile }, 'VPN-Profil aus HVACloud'))}>
            {busy === 'profile' ? 'Lade hoch…' : 'Hochladen und verbinden'}
          </button>
        </div>
      </section>
    </div>
  );
}
